import LFCDialogTitle from '_components/feedback/LFCDialogTitle';
import LFCButton from '_components/inputs/LFCButton';
import ProgressBar from '_components/ProgressBar';
import {setLFCDataProc} from '_logics/LFCUtil';
import {useSnackbar} from 'notistack';
import {type ChangeEvent, type ClipboardEvent, type DragEvent, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';

import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  TextField,
  Typography
} from '@mui/material';

export type FeedbackCategory = 'bug' | 'improvement' | 'feature';

export const feedbackAttachmentMaxBytes = 10 * 1024 * 1024;
export const fallbackAttachmentContentType = 'application/octet-stream';

export type FeedbackAttachmentUpload = {
  key: string;
  fileName: string;
  contentType: string;
  size: number;
};

export type FeedbackAttachmentUploadUrl = {
  key: string;
  url: string;
  fields: Record<string, string>;
  expiresIn: number;
};

export type FeedbackUploadUrlParameters = {
  fileName: string;
  contentType: string;
  size: number;
};

export type FeedbackParameters = {
  companyId: number;
  companyName: string;
  userName: string;
  email: string;
  pageName: string;
  pathname: string;
  category: FeedbackCategory;
  message: string;
  attachment?: FeedbackAttachmentUpload;
};

interface Props {
  open: boolean;
  onClose: () => void;
  companyId: number;
  companyName: string;
  userName: string;
  email: string;
  pageName: string;
  pathname: string;
}

export const buildFeedbackParameters = (parameters: FeedbackParameters): FeedbackParameters => ({
  ...parameters,
  message: parameters.message.trim()
});

export const getFeedbackAttachmentError = (
  file: File | null,
  t: (key: string) => string
): string => {
  if (!file) {
    return '';
  }
  if (file.size > feedbackAttachmentMaxBytes) {
    return t('添付ファイルは10MB以下にしてください');
  }
  return '';
};

export const buildFeedbackUploadUrlParameters = (file: File): FeedbackUploadUrlParameters => ({
  fileName: file.name,
  contentType: file.type || fallbackAttachmentContentType,
  size: file.size
});

export const buildFeedbackAttachment = (
  file: File,
  uploadUrl: FeedbackAttachmentUploadUrl
): FeedbackAttachmentUpload => ({
  key: uploadUrl.key,
  fileName: file.name,
  contentType: file.type || fallbackAttachmentContentType,
  size: file.size
});

export const getClipboardAttachmentFile = (clipboardData: DataTransfer): File | null => {
  if (clipboardData.files.length > 0) {
    return clipboardData.files[0] ?? null;
  }
  const fileItem = Array.from(clipboardData.items).find(item => item.kind === 'file');
  return fileItem?.getAsFile() ?? null;
};

export const isFeedbackSubmitDisabled = (
  message: string,
  isSending: boolean,
  attachmentError = ''
): boolean => {
  return isSending || message.trim() === '' || attachmentError !== '';
};

export const feedbackMessageMaxLength = 2000;

export const shouldCloseFeedbackDialog = (isSending: boolean, force = false): boolean => {
  return force || !isSending;
};

export const feedbackLabelChipProps = {
  size: 'small' as const,
  sx: {alignSelf: 'flex-start'}
};

export const feedbackCategoryLabelKeys: Record<FeedbackCategory, string> = {
  bug: '不具合',
  improvement: '改善要望',
  feature: '追加要望'
};

export const resolveFeedbackPageName = (
  menuData: any[] | undefined,
  pathname: string,
  title: string,
  t: (key: string) => string
): string => {
  const currentMenu = menuData?.find((item: any) => item.path === pathname);
  if (currentMenu?.name) {
    const pageNameKey = `menu.page_name.${currentMenu.name}`;
    const translatedPageName = t(pageNameKey);
    if (translatedPageName !== pageNameKey) {
      return translatedPageName;
    }
  }
  return title || pathname;
};

const FeedbackDialog = (props: Props) => {
  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();
  const [category, setCategory] = useState<FeedbackCategory>('bug');
  const [message, setMessage] = useState('');
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null);
  const [startProcess, setStartProcess] = useState(false);
  const isSubmittingRef = useRef(false);
  const attachmentError = getFeedbackAttachmentError(attachmentFile, t);

  const resetAndClose = (options?: {force?: boolean}) => {
    if (!shouldCloseFeedbackDialog(startProcess, options?.force)) {
      return;
    }
    setCategory('bug');
    setMessage('');
    setAttachmentFile(null);
    props.onClose();
  };

  const onAttachmentChange = (event: ChangeEvent<HTMLInputElement>) => {
    setAttachmentFile(event.target.files?.[0] ?? null);
    event.target.value = '';
  };

  const onMessagePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    const pastedFile = getClipboardAttachmentFile(event.clipboardData);
    if (pastedFile) {
      setAttachmentFile(pastedFile);
    }
  };

  const onMessageDragOver = (event: DragEvent<HTMLInputElement>) => {
    event.preventDefault();
  };

  const onMessageDrop = (event: DragEvent<HTMLInputElement>) => {
    event.preventDefault();
    const droppedFile = getClipboardAttachmentFile(event.dataTransfer);
    if (droppedFile) {
      setAttachmentFile(droppedFile);
    }
  };

  const clearAttachment = () => {
    setAttachmentFile(null);
  };

  const onSubmit = async () => {
    if (
      isSubmittingRef.current ||
      isFeedbackSubmitDisabled(message, startProcess, attachmentError)
    ) {
      return;
    }
    isSubmittingRef.current = true;

    setStartProcess(true);
    try {
      let attachment: FeedbackAttachmentUpload | undefined;
      if (attachmentFile) {
        const uploadUrlResponse = (await setLFCDataProc({
          snack: enqueueSnackbar,
          name: '',
          invoke_name: 'GenerateFeedbackAttachmentUploadUrl',
          parameters: buildFeedbackUploadUrlParameters(attachmentFile),
          t
        })) as FeedbackAttachmentUploadUrl[];
        const uploadUrl = uploadUrlResponse[0];
        if (!uploadUrl) {
          throw new Error('Feedback attachment upload URL was not returned');
        }
        const formData = new FormData();
        Object.entries(uploadUrl.fields).forEach(([key, value]) => {
          formData.append(key, value);
        });
        formData.append('file', attachmentFile);
        const uploadResponse = await fetch(uploadUrl.url, {
          method: 'POST',
          body: formData
        });
        if (!uploadResponse.ok) {
          throw new Error(`S3 upload failed: ${uploadResponse.status}`);
        }
        attachment = buildFeedbackAttachment(attachmentFile, uploadUrl);
      }

      const parameters = buildFeedbackParameters({
        companyId: props.companyId,
        companyName: props.companyName,
        userName: props.userName,
        email: props.email,
        pageName: props.pageName,
        pathname: props.pathname,
        category,
        message,
        attachment
      });

      await setLFCDataProc({
        snack: enqueueSnackbar,
        name: 'フィードバック投稿',
        invoke_name: 'SubmitFeedback',
        parameters,
        t
      });
      resetAndClose({force: true});
    } catch (err) {
      console.error(err);
      enqueueSnackbar(t('フィードバック投稿に失敗しました'), {variant: 'error'});
    } finally {
      isSubmittingRef.current = false;
      setStartProcess(false);
    }
  };

  return (
    <Dialog open={props.open} onClose={() => resetAndClose()} maxWidth="md" fullWidth>
      <ProgressBar startProcess={startProcess} screenLock />
      <LFCDialogTitle onClose={() => resetAndClose()}>{t('フィードバック')}</LFCDialogTitle>
      <DialogContent dividers>
        <FormControl component="section" fullWidth sx={{mb: 2}}>
          <Chip {...feedbackLabelChipProps} label={t('画面名')} />
          <Typography mt={0.5} ml={1} variant="body2">
            {props.pageName} ({props.pathname})
          </Typography>
        </FormControl>
        <FormControl component="fieldset" sx={{mb: 2}}>
          <Chip {...feedbackLabelChipProps} label={t('種別')} />
          <RadioGroup
            row
            sx={{ml: 1}}
            value={category}
            onChange={event => setCategory(event.target.value as FeedbackCategory)}
          >
            <FormControlLabel
              value="bug"
              control={<Radio />}
              label={<Typography variant="body2">{t(feedbackCategoryLabelKeys.bug)}</Typography>}
            />
            <FormControlLabel
              value="improvement"
              control={<Radio />}
              label={
                <Typography variant="body2">{t(feedbackCategoryLabelKeys.improvement)}</Typography>
              }
            />
            <FormControlLabel
              value="feature"
              control={<Radio />}
              label={
                <Typography variant="body2">{t(feedbackCategoryLabelKeys.feature)}</Typography>
              }
            />
          </RadioGroup>
        </FormControl>
        <TextField
          fullWidth
          multiline
          minRows={10}
          label={t('内容')}
          value={message}
          onChange={event => setMessage(event.target.value)}
          onPaste={onMessagePaste}
          onDragOver={onMessageDragOver}
          onDrop={onMessageDrop}
          placeholder={t('フィードバック内容を入力してください')}
          inputProps={{maxLength: feedbackMessageMaxLength}}
        />
        <Box mt={2}>
          <Chip {...feedbackLabelChipProps} label={t('添付ファイル')} />
          <Box mt={1} ml={1} display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <Button component="label" variant="outlined" startIcon={<AttachFileIcon />}>
              {t('ファイルを選択')}
              <input hidden type="file" onChange={onAttachmentChange} />
            </Button>
            {attachmentFile && (
              <>
                <Typography variant="body2">
                  {attachmentFile.name} ({Math.ceil(attachmentFile.size / 1024)} KB)
                </Typography>
                <IconButton
                  size="small"
                  aria-label={t('添付ファイルを削除')}
                  onClick={clearAttachment}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </>
            )}
          </Box>
          <Typography
            mt={0.5}
            ml={1}
            variant="caption"
            color={attachmentError ? 'error' : 'text.secondary'}
          >
            {attachmentError || t('添付ファイルは1件、10MBまで送信できます')}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <LFCButton onClick={() => resetAndClose()}>{t('キャンセル')}</LFCButton>
        <LFCButton
          color="primary"
          disabled={isFeedbackSubmitDisabled(message, startProcess, attachmentError)}
          onClick={onSubmit}
        >
          {t('送信')}
        </LFCButton>
      </DialogActions>
    </Dialog>
  );
};

export default FeedbackDialog;
